import { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, Shield, TrendingUp, Users, Clock, FileText, Home, DollarSign, Phone, Mail, Star, Handshake } from 'lucide-react';
import VisionAireLogo from '@/components/VisionAireLogo';

const HERO_BG = "https://images.pexels.com/photos/15743361/pexels-photo-15743361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BUYER_IMG = "https://images.unsplash.com/photo-1758523671826-d7f8217ffac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxmYW1pbHklMjBuZXclMjBob21lJTIwa2V5fGVufDB8fHx8MTc3NTMyNTQ1Nnww&ixlib=rb-4.1.0&q=85&w=800";
const PROPERTY_IMG = "https://images.pexels.com/photos/7598378/pexels-photo-7598378.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const steps = [
    { num: "01", title: "We Identify a Qualified Buyer", desc: "We find a pre-screened buyer who is genuinely interested in your property and meets our qualification standards." },
    { num: "02", title: "Structure the Owner Finance Agreement", desc: "We structure an Owner Finance Agreement for 12 months of interest-only payments to you, turning your property into an income-generating asset." },
    { num: "03", title: "We Assign the Contract", desc: "We assign that contract to the buyer, making VisionAire Realty Services a legal party to the deal for your protection." },
    { num: "04", title: "You Receive Monthly Payments", desc: "You receive interest-only payments for up to 12 months while the buyer enters credit repair and prepares to refinance." },
    { num: "05", title: "Buyer Refinances, You Get Paid", desc: "The buyer refinances into a conventional mortgage - you receive FULL appraised value at closing. Everyone wins." },
];

const sellerBenefits = [
    { icon: DollarSign, title: "100% Full Appraised Value", desc: "No lowball offers. No price reductions. You get exactly what your property is worth." },
    { icon: TrendingUp, title: "Income Generating Asset", desc: "Your property generates interest-only income payments while the transaction finalizes." },
    { icon: Shield, title: "We Handle the Heavy Lifting", desc: "We assign our Owner Finance Contract to the buyer, handling all legal aspects of the deal." },
    { icon: Clock, title: "Fast, Structured Solution", desc: "Our qualified buyer has up to 12 months to refinance into a traditional mortgage." },
];

const everyoneWins = [
    "Home Seller - Paid FULL Value",
    "Family Obtains a Home",
    "Realtor Gets Paid",
    "Credit Repair - Paid",
    "Mortgage Lender - Paid",
    "Title Company - Paid",
    "Attorney - Paid",
];

export default function LandingPage() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (location.state?.scrollTo) {
            setTimeout(() => {
                const el = document.getElementById(location.state.scrollTo);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    return (
        <div className="min-h-screen" data-testid="landing-page">
            <Navbar variant="dark" />

            {/* ─── HERO (DARK) ───────────────────────────────────────── */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0A0A0A]">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-15" />
                    <div className="hero-overlay absolute inset-0" />
                </div>
                <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-20">
                    <div className="animate-fade-in-up flex justify-center mb-8">
                        <VisionAireLogo size={96} data-testid="hero-logo" />
                    </div>
                    <p className="animate-fade-in-up delay-100 text-xs tracking-[0.3em] uppercase font-bold text-amber-400 mb-6">
                        VisionAire Realty Services
                    </p>
                    <h1 className="animate-fade-in-up delay-200 text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] mb-4 font-['Outfit']">
                        Tired of Waiting to Obtain Your
                    </h1>
                    <h1 className="animate-fade-in-up delay-200 text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.1] mb-8 font-['Outfit'] text-gradient-gold">
                        FULL Appraised Value?
                    </h1>
                    <p className="animate-fade-in-up delay-300 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-4 leading-relaxed">
                        Introducing <span className="text-amber-400 font-semibold">The Owner/Buyer Program</span> - a specialized real estate transaction designed for sellers who refuse to settle for less than their property is worth.
                    </p>
                    <p className="animate-fade-in-up delay-300 text-sm text-zinc-500 mb-10">
                        This service is <span className="text-emerald-400 font-bold">100% FREE</span> to you as the seller. Zero cost. Buyer pays our fee.
                    </p>
                    <div className="animate-fade-in-up delay-400 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button data-testid="hero-seller-cta"
                            onClick={() => navigate('/register?role=seller')}
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-base font-semibold">
                            I'm a Seller - See My Options <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                        <Button data-testid="hero-buyer-cta"
                            onClick={() => navigate('/register?role=buyer')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-base font-semibold">
                            I'm a Buyer - Get Pre-Qualified <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </div>
                    <div className="animate-fade-in-up delay-500 mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
                        {[["ZERO COST", "to Sellers"], ["100%", "Full Appraised Value"], ["12 Months", "Structured Term"]].map(([num, label]) => (
                            <div key={label} className="text-center">
                                <div className="text-xl sm:text-2xl font-semibold text-amber-400 font-['Outfit']">{num}</div>
                                <div className="text-xs text-zinc-500 mt-1">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── SELLER BENEFITS (LIGHT) ───────────────────────────── */}
            <section id="sellers" className="py-24 px-4 sm:px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-600 mb-4 text-center">Why This Program</p>
                    <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900 text-center mb-4 font-['Outfit']">
                        Get Full Price Without Waiting
                    </h2>
                    <p className="text-base text-zinc-500 text-center max-w-2xl mx-auto mb-16">
                        Stop losing money on holding costs. Owner-finance your property through our platform and receive monthly income while getting full appraised value.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {sellerBenefits.map((b, i) => (
                            <div key={i} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-8 hover:border-amber-300 transition-all hover:shadow-lg animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-5">
                                    <b.icon className="w-6 h-6 text-amber-600" />
                                </div>
                                <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">{b.title}</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── HOW IT WORKS (DARK) ───────────────────────────────── */}
            <section id="how-it-works" className="py-24 px-4 sm:px-6 bg-[#0A0A0A]">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-400 mb-4 text-center">The Process</p>
                    <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white text-center mb-16 font-['Outfit']">
                        How Does This Work?
                    </h2>
                    <div className="space-y-6">
                        {steps.map((step, i) => (
                            <div key={i} className="card-dark rounded-2xl p-6 sm:p-8 flex items-start gap-6 animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                                    <span className="text-lg font-bold text-white font-['Outfit']">{step.num}</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium text-white mb-2 font-['Outfit']">{step.title}</h3>
                                    <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── EVERYONE WINS (LIGHT) ─────────────────────────────── */}
            <section className="py-24 px-4 sm:px-6 bg-[#FAFAF8]">
                <div className="max-w-5xl mx-auto">
                    <div className="rounded-3xl border border-zinc-200 bg-white p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center shadow-sm">
                        <div>
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-600 mb-4">Our Philosophy</p>
                            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900 mb-6 font-['Outfit']">
                                When We Close a Deal,<br /><span className="text-amber-600">Everyone Wins</span>
                            </h2>
                            <p className="text-base text-zinc-500 mb-8 leading-relaxed">
                                Seller obtains full value. A family obtains a home. And every professional involved gets paid. That's the VisionAire difference.
                            </p>
                            <div className="space-y-3">
                                {everyoneWins.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                        <span className="text-sm text-zinc-700">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <div className="rounded-2xl overflow-hidden border border-zinc-200 shadow-md">
                                <img src={PROPERTY_IMG} alt="Property" className="w-full h-72 object-cover" />
                            </div>
                            <div className="absolute -bottom-4 -left-4 rounded-xl bg-white border border-zinc-200 shadow-lg p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <Handshake className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-zinc-900">Deal Closed</div>
                                    <div className="text-xs text-zinc-500">Everyone gets paid</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── YOUTUBE / DAY 91 EXPLAINER ───────────────────────── */}
            <section className="py-20 px-4 sm:px-6 bg-[#0A0A0A]">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-3">See The Vision</p>
                        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white mb-3 font-['Outfit']">
                            The Day 91 Concept
                        </h2>
                        <p className="text-base text-zinc-400 max-w-2xl mx-auto">
                            When a home doesn't sell after 90 days on market, VisionAire steps in. Watch how our marketplace automation creates deals where traditional methods fail.
                        </p>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                        <div className="aspect-video">
                            <video
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_explainer_final.mp4?v=2`}
                                title="VisionAire Realty Services - The Day 91 Concept"
                                controls
                                
                                muted
                                
                                playsInline
                                poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_poster.jpg?v=2`}
                                className="w-full h-full object-cover"
                                data-testid="landing-video"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-8">
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-amber-400 font-['Outfit']">Day 75</div>
                            <div className="text-xs text-zinc-400 mt-1">Agents enroll listings into the system</div>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-amber-400 font-['Outfit']">Day 91</div>
                            <div className="text-xs text-zinc-400 mt-1">Listings auto-activate on marketplace</div>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-emerald-400 font-['Outfit']">Close</div>
                            <div className="text-xs text-zinc-400 mt-1">Operators match buyers and close</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── FOR BUYERS (DARK) ─────────────────────────────────── */}
            <section id="buyers" className="py-24 px-4 sm:px-6 bg-[#0A0A0A]">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-400 mb-4">For Buyers</p>
                        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white mb-6 font-['Outfit']">
                            Own a Home Without<br />Perfect Credit
                        </h2>
                        <p className="text-base text-zinc-400 mb-8 leading-relaxed">
                            Traditional banks say no. We say let's find a way. Through our owner-financed program,
                            you move in now and refinance within 12 months while rebuilding your credit.
                        </p>
                        <ul className="space-y-3 mb-8">
                            {[
                                "No perfect credit required",
                                "Owner-financed terms, not bank terms",
                                "12-month path to refinance",
                                "Credit repair support included",
                                "Platform-structured for your protection",
                            ].map((b, i) => (
                                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <Button data-testid="buyer-section-cta" onClick={() => navigate('/register?role=buyer')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-5 font-semibold">
                            Get Pre-Qualified <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </div>
                    <div className="relative">
                        <div className="rounded-2xl overflow-hidden border border-white/10">
                            <img src={BUYER_IMG} alt="Family in new home" className="w-full h-80 object-cover" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 card-dark rounded-xl p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-700/20 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-white">Pre-Qualified</div>
                                <div className="text-xs text-zinc-400">In under 5 minutes</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── CURRENTLY LISTED FAQ (LIGHT) ──────────────────────── */}
            <section className="py-16 px-4 sm:px-6 bg-white">
                <div className="max-w-3xl mx-auto rounded-2xl border border-amber-200 bg-amber-50/50 p-8 sm:p-10">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                            <Star className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="text-xl font-medium text-zinc-900 mb-3 font-['Outfit']">Currently Listed With a Realtor?</h3>
                            <p className="text-sm text-zinc-600 leading-relaxed">
                                Great — we love working with realtors! VisionAire partners directly with your agent to expand their selling options through our owner-finance marketplace.
                                Your realtor stays involved throughout the entire process, and together we create more paths to get your property <span className="text-zinc-900 font-semibold">SOLD</span>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── QUOTE (DARK) ──────────────────────────────────────── */}
            <section className="py-20 px-4 sm:px-6 bg-[#0A0A0A]">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="text-5xl text-amber-400/30 mb-4 font-serif">"</div>
                    <p className="text-lg sm:text-xl text-zinc-300 italic leading-relaxed mb-6">
                        If you are a seller who refuses to concede from full appraised value - and rightfully so -
                        this program was built for you. You no longer have to choose between waiting indefinitely
                        or accepting less than your property is worth.
                    </p>
                    <p className="text-sm text-amber-400 font-bold uppercase tracking-[0.15em]">
                        This Program Was Built For You
                    </p>
                </div>
            </section>

            {/* ─── 12-MONTH TIMELINE (LIGHT) ─────────────────────────── */}
            <section className="py-24 px-4 sm:px-6 bg-[#FAFAF8]">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 mb-4">Buyer Journey</p>
                    <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900 mb-16 font-['Outfit']">
                        12-Month Path to Ownership
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                        {[
                            { month: "Month 1", title: "Move In", desc: "Sign agreement, make down payment, move into your new home.", icon: Home },
                            { month: "Months 2-6", title: "Build Credit", desc: "Make on-time payments. Credit repair services rebuild your score.", icon: TrendingUp },
                            { month: "Months 7-11", title: "Prepare", desc: "Connect with mortgage lenders. Get ready for conventional financing.", icon: FileText },
                            { month: "Month 12", title: "Refinance", desc: "Complete refinance into traditional mortgage. Full ownership is yours.", icon: Shield },
                        ].map((item, i) => (
                            <div key={i} className="rounded-2xl border border-zinc-200 bg-white p-6 text-left shadow-sm hover:shadow-md hover:border-emerald-300 transition-all">
                                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                                    <item.icon className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div className="text-xs text-emerald-600 font-bold mb-1">{item.month}</div>
                                <h4 className="text-base font-medium text-zinc-900 mb-2 font-['Outfit']">{item.title}</h4>
                                <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── CTA (DARK) ────────────────────────────────────────── */}
            <section className="py-24 px-4 sm:px-6 bg-[#0A0A0A]">
                <div className="max-w-3xl mx-auto card-dark rounded-3xl p-12 text-center">
                    <VisionAireLogo size={64} className="mx-auto mb-6" />
                    <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white mb-4 font-['Outfit']">
                        Ready to Get Started?
                    </h2>
                    <p className="text-base text-zinc-400 mb-8">
                        Whether you're looking to sell at full value or buy your first home, we'll structure the perfect deal.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                        <Button data-testid="cta-seller" onClick={() => navigate('/register?role=seller')}
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-5 font-semibold">
                            I Want to Sell
                        </Button>
                        <Button data-testid="cta-buyer" onClick={() => navigate('/register?role=buyer')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-5 font-semibold">
                            I Want to Buy
                        </Button>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-sm text-zinc-400">
                        <a href="tel:7139558928" className="flex items-center gap-2 hover:text-amber-400 transition-colors" data-testid="contact-phone">
                            <Phone className="w-4 h-4" /> 713-955-8928
                        </a>
                        <a href="mailto:contact@visionairerealty.com" className="flex items-center gap-2 hover:text-amber-400 transition-colors" data-testid="contact-email">
                            <Mail className="w-4 h-4" /> contact@visionairerealty.com
                        </a>
                    </div>
                </div>
            </section>

            {/* ─── FOOTER (DARK) ─────────────────────────────────────── */}
            <footer className="border-t border-white/10 py-16 px-4 sm:px-6 bg-[#0A0A0A]" data-testid="landing-footer">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
                        {/* Brand */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <VisionAireLogo size={32} />
                                <span className="text-sm font-semibold text-white font-['Outfit'] tracking-[0.08em] uppercase">VisionAire Realty Services</span>
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed mb-4">
                                Connecting property sellers with qualified buyers through innovative owner-financed transactions.
                            </p>
                            <div className="space-y-2">
                                <a href="tel:7139558928" className="flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors">
                                    <Phone className="w-3 h-3" /> 713-955-8928
                                </a>
                                <a href="mailto:contact@visionairerealty.com" className="flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors">
                                    <Mail className="w-3 h-3" /> contact@visionairerealty.com
                                </a>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-4">Platform</h4>
                            <div className="space-y-2.5">
                                {[
                                    { label: 'Marketplace', to: '/marketplace' },
                                    { label: 'For Sellers', href: '#sellers' },
                                    { label: 'For Buyers', href: '#buyers' },
                                    { label: 'Feature Guide', to: '/guide' },
                                ].map(link => (
                                    link.to ?
                                        <a key={link.label} href={link.to} className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">{link.label}</a> :
                                        <a key={link.label} href={link.href} className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">{link.label}</a>
                                ))}
                            </div>
                        </div>

                        {/* Legal */}
                        <div>
                            <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-4">Legal</h4>
                            <div className="space-y-2.5">
                                {[
                                    { label: 'Terms of Service', doc: 'terms' },
                                    { label: 'Privacy Policy', doc: 'privacy' },
                                    { label: 'Legal Disclaimer', doc: 'disclaimer' },
                                    { label: 'FCRA Disclosure', doc: 'fcra' },
                                    { label: 'Cookie Policy', doc: 'cookies' },
                                ].map(link => (
                                    <a key={link.doc} href={`/legal?doc=${link.doc}`} data-testid={`footer-legal-${link.doc}`}
                                        className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">{link.label}</a>
                                ))}
                            </div>
                        </div>

                        {/* Resources */}
                        <div>
                            <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-4">Resources</h4>
                            <div className="space-y-2.5">
                                <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">Free Credit Reports</a>
                                <a href="https://www.consumerfinance.gov" target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">CFPB</a>
                                <a href="https://www.ftc.gov/credit" target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">FTC Credit Resources</a>
                                <a href="https://www.identitytheft.gov" target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-500 hover:text-amber-400 transition-colors">Identity Theft Help</a>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} VisionAire Realty Services LLC. All rights reserved.</p>
                        <div className="flex items-center gap-4 text-xs text-zinc-600">
                            <a href="/legal?doc=terms" className="hover:text-zinc-400 transition-colors">Terms</a>
                            <span className="text-zinc-700">|</span>
                            <a href="/legal?doc=privacy" className="hover:text-zinc-400 transition-colors">Privacy</a>
                            <span className="text-zinc-700">|</span>
                            <a href="/legal?doc=fcra" className="hover:text-zinc-400 transition-colors">FCRA</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
